/**
 * Files attached in the composer (slice 5b spec §6.2): an attach button
 * beside Send, each file uploaded as it's picked, shown as a chip above the
 * words (its progress, why it failed, ×), and sent with the message by id.
 * Up to 4 photos and 10 files a message; Send waits while any uploads.
 * Files pasted into the words or dropped on the box are picked the same way.
 * A photo shows as a thumbnail with its × in the corner; tapped, it opens
 * full size, as a sent one does in the thread.
 */
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from "@crew/ui/components/attachment";
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogTitle } from "@crew/ui/components/dialog";
import { InputGroupButton } from "@crew/ui/components/input-group";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Paperclip, X } from "lucide-react";
import { useRef, useState, type ClipboardEvent, type ReactNode } from "react";
import type { Uploaded } from "../../app/api";
import { fileUrl } from "../messaging/attached";
import { ACCEPT, kindOf, sizeLabel } from "./kinds";
import { droppedFiles, pastedFiles } from "./pasted";
import { useUploads, type Upload } from "./use-uploads";

/** The most a message carries (spec §6.2). */
const MAX_PHOTOS = 4;
const MAX_FILES = 10;

/** A message's body with no words, as the Worker names it ("📷 Photo", "📎 Lease.pdf", "📎 2 files"). */
/** An Edited message's words for the composer: none when its files only named it (Devin on #435). */
export const editedBody = (body: string, files: Pick<Uploaded, "name" | "kind">[]) =>
  files.length && body === wordlessBody(files) ? "" : body;

export function wordlessBody(files: Pick<Uploaded, "name" | "kind">[]): string {
  const photos = files.filter((f) => f.kind === "photo").length;
  if (photos === files.length && photos > 0)
    return photos === 1 ? "📷 Photo" : `📷 ${photos} photos`;
  if (files.length === 1) return `📎 ${files[0]!.name}`;
  return `📎 ${files.length} files`;
}

/** Why more can't be attached, or null: past 4 photos or 10 files. */
function overLimit(list: Upload[], picked: File[]): string | null {
  const live = list.filter((u) => u.state !== "failed");
  const photos = live.filter((u) => u.mime.startsWith("image/")).length;
  const pickedPhotos = picked.filter((f) => f.type.startsWith("image/")).length;
  if (photos + pickedPhotos > MAX_PHOTOS) return `A message takes ${MAX_PHOTOS} photos at most.`;
  if (live.length - photos + picked.length - pickedPhotos > MAX_FILES) {
    return `A message takes ${MAX_FILES} files at most.`;
  }
  return null;
}

/** The composer's files: picked, uploading, ready to send, and why more can't be added. */
export function useComposerFiles() {
  const uploads = useUploads();
  const [problem, setProblem] = useState<string | null>(null);
  // Past the limits already: an Edited start brings back its replies' files too
  // (Codex on #435). Send waits until some are removed.
  const over = overLimit(uploads.list, []);
  return {
    ...uploads,
    problem: problem ?? over,
    over: over !== null,
    pick: (files: File[]) => {
      const over = overLimit(uploads.list, files);
      setProblem(over);
      if (!over) void uploads.add(files);
    },
    put: uploads.put,
    clear: () => {
      setProblem(null);
      uploads.clear();
    },
  };
}

/** The attach button, beside Send. */
function AttachButton({
  onFiles,
  disabled,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <InputGroupButton
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Attach files"
        data-attach-button
        disabled={disabled}
        onClick={() => input.current?.click()}
        className="size-9 rounded-md text-muted-foreground md:size-7"
      >
        <Paperclip />
      </InputGroupButton>
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept={ACCEPT}
        data-attach-input
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
    </>
  );
}

/** Where a photo's shown from: its own bytes while picked here, else Crew's copy (an Edited message's). */
function photoSrc(upload: Upload, size: "thumb" | "full"): string | null {
  if (upload.preview) return upload.preview;
  if (upload.file && upload.mime.startsWith("image/")) return fileUrl(upload.file.id, size);
  return null;
}

/** A photo's chip: its thumbnail, a spinner over it while it uploads, and its × in the corner. */
function PhotoChip({
  upload,
  src,
  onOpen,
  onRemove,
}: {
  upload: Upload;
  src: string;
  onOpen: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      data-composer-file={upload.state}
      data-composer-photo
      className="relative size-16 shrink-0"
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${upload.name}`}
        title={upload.name}
        className="block size-full cursor-zoom-in overflow-hidden rounded-lg bg-muted shadow-ring focus-visible:outline-2 focus-visible:outline-ring"
      >
        <img
          src={src}
          alt=""
          decoding="async"
          className={cn("size-full object-cover", upload.state === "uploading" && "opacity-60")}
        />
        {upload.state === "uploading" ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </span>
        ) : null}
      </button>
      <button
        type="button"
        aria-label={`Remove ${upload.name}`}
        onClick={onRemove}
        className="absolute top-1 right-1 flex size-6 items-center justify-center rounded-md bg-foreground/75 text-background hover:bg-foreground/90 focus-visible:outline-2 focus-visible:outline-ring md:size-5"
      >
        <X className="size-3.5 md:size-3" strokeWidth={2.25} />
      </button>
    </div>
  );
}

function FileChip({ upload, onRemove }: { upload: Upload; onRemove: () => void }) {
  const kind = kindOf({ mime: upload.mime, name: upload.name });
  const Icon = kind.icon;
  const state =
    upload.state === "uploading" ? "uploading" : upload.state === "failed" ? "error" : "done";
  return (
    <Attachment size="xs" state={state} data-composer-file={upload.state} className="max-w-[240px]">
      <AttachmentMedia>{upload.state === "uploading" ? <Spinner /> : <Icon />}</AttachmentMedia>
      <AttachmentContent className="min-w-0">
        <AttachmentTitle className="truncate">{upload.name}</AttachmentTitle>
        <AttachmentDescription className={state === "error" ? "text-brick" : undefined}>
          {upload.state === "failed" ? upload.hint : sizeLabel(upload.size)}
        </AttachmentDescription>
      </AttachmentContent>
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={`Remove ${upload.name}`}
        onClick={onRemove}
      >
        <X />
      </Button>
    </Attachment>
  );
}

/** A paste that's files attaches them, as the attach button would; any other pastes as usual. */
export function pasteFiles(pick: (files: File[]) => void) {
  return (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = pastedFiles(event.clipboardData);
    if (!files.length) return;
    event.preventDefault();
    pick(files);
  };
}

/**
 * What a composer takes for its files (slice 5b spec §6.2): the chips (with
 * the doc chip, if any), the attach button, a paste or drop of files, and
 * whether Send may go now. A closed composer takes no files, by any way.
 */
export function filesProps(
  files: ReturnType<typeof useComposerFiles>,
  doc: ReactNode | undefined,
  open: boolean,
) {
  return {
    attachment: composerAttachment(files, doc),
    tools: <AttachButton onFiles={files.pick} disabled={!open} />,
    onPaste: open ? pasteFiles(files.pick) : undefined,
    onDropFiles: open
      ? (data: DataTransfer) => {
          const dropped = droppedFiles(data);
          if (dropped.length) files.pick(dropped);
        }
      : undefined,
    sendable: files.ready.length > 0,
    holdSend: files.busy || files.over,
  };
}

/**
 * The composer's `attachment`: the chips above the words, with the doc chip
 * if there's one, and why more can't be added; undefined with none of them.
 */
function composerAttachment(
  files: ReturnType<typeof useComposerFiles>,
  doc?: ReactNode,
): ReactNode | undefined {
  if (files.list.length === 0 && !doc && !files.problem) return undefined;
  return <ComposerAttachments files={files} doc={doc} />;
}

function ComposerAttachments({
  files,
  doc,
}: {
  files: ReturnType<typeof useComposerFiles>;
  doc?: ReactNode;
}) {
  const [open, setOpen] = useState<Upload | null>(null);
  const full = open ? photoSrc(open, "full") : null;
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-composer-attachments>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {doc}
        {files.list.map((u) => {
          const src = u.state === "failed" ? null : photoSrc(u, "thumb");
          const remove = () => files.remove(u.key);
          return src ? (
            <PhotoChip
              key={u.key}
              upload={u}
              src={src}
              onOpen={() => setOpen(u)}
              onRemove={remove}
            />
          ) : (
            <FileChip key={u.key} upload={u} onRemove={remove} />
          );
        })}
      </div>
      {files.problem ? (
        <p role="alert" className="px-1 text-meta text-brick">
          {files.problem}
        </p>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(next) => (next ? null : setOpen(null))}>
        <DialogContent
          data-composer-photo-preview
          className="w-auto max-w-[calc(100vw-2rem)] gap-2 p-2 sm:max-w-[min(90vw,1100px)]"
        >
          <DialogTitle className="sr-only">{open?.name ?? "Photo"}</DialogTitle>
          {full ? (
            <img
              src={full}
              alt={open?.name ?? ""}
              className="h-auto max-h-[85vh] w-auto max-w-[calc(100vw-3rem)] rounded-lg object-contain sm:max-w-[min(88vw,1084px)]"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
